import { t } from '../i18n'
import { renderStackupInWorker, type StackupLayerInput, type StackupResult } from './stackupWorker'
import type {
  GerberFile,
  LayerCheck,
  LayerMapping,
  LayerSide,
  LayerType,
  ProcessedLayer,
  RenderResult,
  RenderWarning,
  StoredFile,
} from '../types'

let fileId = 0

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

const KNOWN_EXTENSIONS = new Set([
  'gbr', 'ger', 'gtl', 'gbl', 'gto', 'gbo', 'gts', 'gbs', 'gtp', 'gbp',
  'gm1', 'gko', 'gml', 'drl', 'nc', 'xln', 'drd', 'art', 'pho', 'cmp',
  'sol', 'stc', 'sts', 'plc', 'pls', 'txt',
])

export function isKnownGerberName(name: string): boolean {
  const extension = name.split('.').pop()?.toLowerCase() ?? ''
  return KNOWN_EXTENSIONS.has(extension) || /^\d{1,3}$/.test(extension)
}

const EXTENSION_MAP: Record<string, LayerMapping> = {
  gtl: { side: 'top', type: 'copper' },
  gbl: { side: 'bottom', type: 'copper' },
  gto: { side: 'top', type: 'silkscreen' },
  gbo: { side: 'bottom', type: 'silkscreen' },
  gts: { side: 'top', type: 'soldermask' },
  gbs: { side: 'bottom', type: 'soldermask' },
  gtp: { side: 'top', type: 'solderpaste' },
  gbp: { side: 'bottom', type: 'solderpaste' },
  gm1: { side: 'all', type: 'outline' },
  gko: { side: 'all', type: 'outline' },
  gml: { side: 'all', type: 'outline' },
  drl: { side: 'all', type: 'drill' },
  nc: { side: 'all', type: 'drill' },
  xln: { side: 'all', type: 'drill' },
  drd: { side: 'all', type: 'drill' },
}

export interface CsvFile {
  name: string
  text: string
}

export interface ReadGerberResult {
  files: GerberFile[]
  skipped: string[]
  csvFiles: CsvFile[]
}

/**
 * tracespace UMD bundles are ~200 KB. They are loaded on demand, only when a
 * board is actually opened, so the first paint stays light.
 */
let vendorPromise: Promise<void> | null = null

export function loadGerberVendor(): Promise<void> {
  if (typeof window.pcbStackup === 'function' && window.whatsThatGerber) return Promise.resolve()
  if (vendorPromise) return vendorPromise
  vendorPromise = Promise.all([
    loadScript('/vendor/whats-that-gerber.min.js'),
    loadScript('/vendor/pcb-stackup.min.js'),
  ]).then(() => undefined)
  return vendorPromise
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-vendor="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error(t('error.renderer'))), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.vendor = src
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error(t('error.renderer'))), { once: true })
    document.head.append(script)
  })
}

function normalizeMapping(mapping: Partial<LayerMapping> | null | undefined): LayerMapping {
  const side = (mapping?.side ?? null) as LayerSide
  let type = (mapping?.type ?? null) as LayerType
  if (type === 'drill' || type === 'outline') {
    return { side: 'all', type }
  }
  if (type === 'drawing') {
    return { side: null, type }
  }
  return { side, type }
}

export function mappingOf(file: GerberFile): LayerMapping {
  return file.override ?? file.detected
}

export function detectLayer(name: string, text = ''): LayerMapping {
  try {
    const detected = window.whatsThatGerber?.([name])?.[name]
    if (detected?.type) {
      return normalizeMapping(detected as Partial<LayerMapping>)
    }
  } catch {
    // Filename heuristics below are the fallback.
  }

  const extension = name.split('.').pop()?.toLowerCase() ?? ''
  if (EXTENSION_MAP[extension]) {
    return EXTENSION_MAP[extension]
  }

  const head = text.slice(0, 1200).trim()
  if (/^M48/m.test(head) || /^;FILE_FORMAT/m.test(head)) {
    return { side: 'all', type: 'drill' }
  }

  return { side: null, type: null }
}

function isGerberLike(name: string, text: string): boolean {
  const lower = name.toLowerCase()
  if (
    lower.includes('__macosx/') ||
    lower.endsWith('.ds_store') ||
    lower.endsWith('thumbs.db') ||
    lower.endsWith('.pdf') ||
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.zip') ||
    lower.endsWith('.xlsx') ||
    lower.endsWith('.xls') ||
    lower.endsWith('.csv') ||
    lower.endsWith('.apr') ||
    lower.endsWith('.apr_lib') ||
    lower.endsWith('.drr') ||
    lower.endsWith('.extrep') ||
    lower.endsWith('.rep') ||
    lower.endsWith('.rul') ||
    lower.endsWith('.ldp') ||
    lower.endsWith('.dri')
  ) {
    return false
  }

  const extension = lower.split('.').pop() ?? ''
  const head = text.slice(0, 1600)
  const looksLikeGerber =
    head.includes('%FS') ||
    head.includes('%MO') ||
    head.includes('%ADD') ||
    /^G04/m.test(head) ||
    /^M48/m.test(head)
  const looksLikeExcellon = /^M48/m.test(head) || /^T\d+C/m.test(head) || /^G90/m.test(head)

  if (KNOWN_EXTENSIONS.has(extension)) {
    // A .txt file can be a drill file or notes. Require a format marker.
    if (extension === 'txt') return looksLikeGerber || looksLikeExcellon
    return text.trim().length > 0
  }

  return looksLikeGerber || looksLikeExcellon
}

function makeFile(name: string, text: string, size: number, index: number): GerberFile {
  return {
    id: `f${++fileId}-${index}`,
    name,
    text,
    size,
    detected: detectLayer(name, text),
    override: null,
    enabled: true,
  }
}

function dedupeName(name: string, used: Set<string>): string {
  if (!used.has(name)) {
    used.add(name)
    return name
  }
  const dot = name.lastIndexOf('.')
  const base = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  let counter = 2
  let candidate = `${base}-${counter}${ext}`
  while (used.has(candidate)) {
    counter += 1
    candidate = `${base}-${counter}${ext}`
  }
  used.add(candidate)
  return candidate
}

export interface ZipEntry {
  path: string
  text: string
  size: number
}

export async function unzipFile(file: File): Promise<ZipEntry[]> {
  return unzipInWorker(await file.arrayBuffer())
}

function unzipInWorker(buffer: ArrayBuffer): Promise<ZipEntry[]> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../workers/unzip.worker.ts', import.meta.url), { type: 'module' })
    const finish = (): void => worker.terminate()
    worker.onmessage = (event: MessageEvent<{ ok: boolean; entries?: ZipEntry[]; error?: string }>) => {
      finish()
      if (event.data.ok && event.data.entries) resolve(event.data.entries)
      else reject(new Error(event.data.error ?? 'zip'))
    }
    worker.onerror = () => {
      finish()
      reject(new Error('zip'))
    }
    worker.postMessage(buffer, [buffer])
  })
}

export async function readGerberFiles(files: File[]): Promise<ReadGerberResult> {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
  if (totalBytes > MAX_UPLOAD_BYTES) {
    throw new Error(t('error.tooLarge', { mb: Math.round(MAX_UPLOAD_BYTES / 1024 / 1024) }))
  }

  const out: GerberFile[] = []
  const skipped: string[] = []
  const csvFiles: CsvFile[] = []
  const used = new Set<string>()
  let index = 0

  for (const file of files) {
    const lower = file.name.toLowerCase()

    if (lower.endsWith('.zip')) {
      let entries: ZipEntry[]
      try {
        entries = await unzipInWorker(await file.arrayBuffer())
      } catch {
        throw new Error(t('error.invalidZip', { name: file.name }))
      }

      for (const entry of entries) {
        const base = entry.path.split('/').pop() ?? entry.path
        if (!base || base.startsWith('.')) continue
        if (base.toLowerCase().endsWith('.csv')) {
          csvFiles.push({ name: base, text: entry.text })
          continue
        }
        if (!isGerberLike(base, entry.text)) {
          if (!entry.path.toLowerCase().includes('__macosx/')) skipped.push(base)
          continue
        }
        out.push(makeFile(dedupeName(base, used), entry.text, entry.size, index++))
      }
      continue
    }

    if (lower.endsWith('.csv')) {
      csvFiles.push({ name: file.name, text: await file.text() })
      continue
    }

    const text = await file.text()
    if (!isGerberLike(file.name, text)) {
      skipped.push(file.name)
      continue
    }
    out.push(makeFile(dedupeName(file.name, used), text, file.size, index++))
  }

  if (out.length === 0) {
    throw new Error(t('error.noGerber'))
  }

  out.sort((a, b) => {
    const order: Record<string, number> = {
      outline: 0,
      copper: 1,
      soldermask: 2,
      silkscreen: 3,
      solderpaste: 4,
      drill: 5,
      drawing: 6,
    }
    const aType = mappingOf(a).type ?? 'drawing'
    const bType = mappingOf(b).type ?? 'drawing'
    return (order[aType] ?? 9) - (order[bType] ?? 9) || a.name.localeCompare(b.name)
  })

  return { files: out, skipped, csvFiles }
}

export function filesFromStored(stored: StoredFile[]): GerberFile[] {
  return stored.map((file, index) => ({
    id: `f${++fileId}-${index}`,
    name: file.name,
    text: file.text,
    size: file.size,
    // Older saves may predate vendor detection; re-detect anything unmapped.
    detected: file.type ? { side: file.side, type: file.type } : detectLayer(file.name, file.text),
    override: null,
    enabled: file.enabled,
  }))
}

export function storedFromFiles(files: GerberFile[]): StoredFile[] {
  return files.map((file) => {
    const mapping = mappingOf(file)
    return {
      name: file.name,
      text: file.text,
      size: file.size,
      side: mapping.side,
      type: mapping.type,
      enabled: file.enabled,
    }
  })
}

export function themeColors(mask: string): Record<string, string> {
  const themes: Record<string, Record<string, string>> = {
    green: {
      fr4: '#0c1110',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(9, 42, 26, 0.72)',
      ss: '#e9e4d9',
      sp: '#8d97a1',
      out: '#040607',
    },
    black: {
      fr4: '#0b0d10',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(13, 15, 18, 0.84)',
      ss: '#e9e4d9',
      sp: '#8d97a1',
      out: '#040607',
    },
    matte_black: {
      fr4: '#090b0d',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(5, 6, 7, 0.9)',
      ss: '#d8d4ca',
      sp: '#8d97a1',
      out: '#040607',
    },
    blue: {
      fr4: '#0d1218',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(10, 28, 62, 0.74)',
      ss: '#e9e4d9',
      sp: '#8d97a1',
      out: '#040607',
    },
    red: {
      fr4: '#140e10',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(58, 12, 14, 0.72)',
      ss: '#e9e4d9',
      sp: '#8d97a1',
      out: '#040607',
    },
    white: {
      fr4: '#15181b',
      cu: '#c98a4e',
      cf: '#e8b077',
      sm: 'rgba(214, 212, 202, 0.6)',
      ss: '#101317',
      sp: '#8d97a1',
      out: '#040607',
    },
  }
  return themes[mask] ?? themes.green
}

/**
 * Chromium can drop the entire board when a 3D CSS transform is applied to an
 * SVG that uses <mask>/<clipPath>. Top/bottom keep the masks; perspective uses
 * this flattened variant.
 */
export function stripMasks(svg: string): string {
  return svg
    .replace(/\s+mask="url\(#[^"]+\)"/g, '')
    .replace(/\s+clip-path="url\(#[^"]+\)"/g, '')
}

/**
 * Returns how much of the SVG viewBox is covered by the first outline mask.
 * A panelised Gerber with several board outlines gets a mask that covers only
 * one board, so pcb-stackup hides the rest of the panel. Duplicate outline
 * layers cover the whole viewBox and are safe to clip.
 */
function maskCoverage(svg: string): number | null {
  const viewBox = svg.match(/viewBox="([^"]+)"/)?.[1].split(/\s+/).map(Number)
  if (!viewBox || viewBox.length !== 4) return null
  const rect = svg.match(/<mask[^>]*>[\s\S]*?<rect[^>]*x="([^"]+)"[^>]*y="([^"]+)"[^>]*width="([^"]+)"[^>]*height="([^"]+)"/)
  if (!rect) return null
  const width = Number(rect[3])
  const height = Number(rect[4])
  const boxWidth = viewBox[2]
  const boxHeight = viewBox[3]
  if (!Number.isFinite(width) || !Number.isFinite(height) || boxWidth <= 0 || boxHeight <= 0) return null
  return (width * height) / (boxWidth * boxHeight)
}

function activeMappings(files: GerberFile[]): LayerMapping[] {
  return files
    .filter((file) => file.enabled)
    .map(mappingOf)
    .filter((mapping) => mapping.type !== null)
}

function countDrillHits(files: GerberFile[]): number {
  let hits = 0
  for (const file of files) {
    if (!file.enabled) continue
    const mapping = mappingOf(file)
    if (mapping.type !== 'drill') continue
    const lines = file.text.split(/\r?\n/)
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith(';')) continue
      if (/^[XY][-+]?\d/.test(trimmed)) hits += 1
    }
  }
  return hits
}

function minAperture(files: GerberFile[]): number | null {
  let smallest: number | null = null
  for (const file of files) {
    if (!file.enabled) continue
    const mapping = mappingOf(file)
    if (mapping.type !== 'copper') continue
    const isInch = /%MOIN/i.test(file.text.slice(0, 2000))
    const matches = file.text.matchAll(/%ADD\d+C,([0-9.]+)\*%/gi)
    for (const match of matches) {
      const value = Number(match[1])
      if (!Number.isFinite(value) || value <= 0) continue
      const mm = isInch ? value * 25.4 : value
      if (smallest === null || mm < smallest) smallest = mm
    }
  }
  return smallest
}

export interface RenderDiagnostics {
  checks: LayerCheck[]
  warnings: RenderWarning[]
  drillHits: number
  minApertureMm: number | null
}

/**
 * Checks and stats that depend only on which files are enabled. The 3D view
 * toggles visibility without re-running pcb-stackup, so it uses this to keep
 * the quote panel from showing stale "missing layer" warnings.
 */
export function diagnose(
  files: GerberFile[],
  copperLayers: number,
  panelDetected: boolean,
): RenderDiagnostics {
  const mappings = activeMappings(files)
  const mappedCopper = mappings.filter((mapping) => mapping.type === 'copper').length
  const effectiveCopper = Math.max(copperLayers, mappedCopper)
  const checks = buildChecks(files, effectiveCopper)
  const warnings: RenderWarning[] = []
  const unmapped = files.filter((file) => mappingOf(file).type === null).length
  if (unmapped > 0) warnings.push({ key: 'unmapped', n: unmapped })
  if (panelDetected) warnings.push({ key: 'panel' })
  if (!checks.some((check) => check.key === 'outline' && check.ok)) warnings.push({ key: 'noOutline' })
  if (!checks.some((check) => check.key === 'drill' && check.ok)) warnings.push({ key: 'noDrill' })
  return {
    checks,
    warnings,
    drillHits: countDrillHits(files),
    minApertureMm: minAperture(files),
  }
}

function buildChecks(files: GerberFile[], copperLayers: number): LayerCheck[] {
  const mappings = activeMappings(files)
  const has = (side: LayerSide, type: LayerType): boolean =>
    mappings.some(
      (mapping) =>
        mapping.type === type &&
        (mapping.side === side || mapping.side === 'all' || (side === 'top' && mapping.side === null)),
    )

  const checks: LayerCheck[] = [
    { key: 'topCopper', ok: has('top', 'copper') },
    { key: 'outline', ok: has('all', 'outline') },
    { key: 'drill', ok: has('all', 'drill') },
    { key: 'topMask', ok: has('top', 'soldermask') },
    { key: 'topSilk', ok: has('top', 'silkscreen') },
  ]

  if (copperLayers > 1) {
    checks.splice(1, 0, { key: 'bottomCopper', ok: has('bottom', 'copper') })
  }
  if (copperLayers > 2) {
    checks.push({ key: 'innerCopper', ok: mappings.filter((m) => m.type === 'copper').length > 2 })
  }
  return checks
}

/**
 * Parsing a dense panel takes pcb-stackup seconds of CPU. Run it in the worker
 * so the spinner and the rest of the UI stay alive; fall back to the main
 * thread only when workers or the vendor bundle are unavailable.
 */
async function runStackup(
  layers: StackupLayerInput[],
  options: { color?: Record<string, string>; useOutline: boolean },
): Promise<StackupResult> {
  try {
    return await renderStackupInWorker(layers, options)
  } catch {
    // fall through to the main-thread bundle
  }
  await loadGerberVendor()
  if (typeof window.pcbStackup !== 'function') {
    throw new Error(t('error.renderer'))
  }
  const stackup = await window.pcbStackup(layers, options)
  return {
    top: stackup.top,
    bottom: stackup.bottom,
    layers: (stackup.layers ?? []) as ProcessedLayer[],
  }
}

export async function renderStackup(
  files: GerberFile[],
  colors: Record<string, string>,
  options: { knownPanel?: boolean } = {},
): Promise<RenderResult> {
  if (typeof window.pcbStackup !== 'function') {
    throw new Error(t('error.renderer'))
  }

  const active = files.filter((file) => {
    const mapping = mappingOf(file)
    return file.enabled && mapping.type !== null && mapping.type !== 'drawing'
  })

  if (active.length === 0) {
    throw new Error(t('error.noMapped'))
  }

  const outlineCount = active.filter((file) => mappingOf(file).type === 'outline').length
  const useOutline = outlineCount > 0

  const layers: StackupLayerInput[] = active.map((file) => {
    const mapping = mappingOf(file)
    const layer: StackupLayerInput = {
      filename: file.name,
      gerber: file.text,
    }
    if (mapping.type) layer.type = mapping.type
    if (mapping.side) layer.side = mapping.side
    return layer
  })

  let stackup: StackupResult
  let panelDetected = false
  if (options.knownPanel && useOutline) {
    // The first render already proved this layout is a panel; skip the
    // outline-clipped pass and parse it once in panel mode.
    panelDetected = true
    stackup = await runStackup(layers, { color: colors, useOutline: false })
  } else {
    try {
      stackup = await runStackup(layers, { color: colors, useOutline })
      if (useOutline) {
        const coverage = maskCoverage(stackup.top.svg)
        if (coverage !== null && coverage < 0.85) {
          panelDetected = true
          stackup = await runStackup(layers, { color: colors, useOutline: false })
        }
      }
    } catch {
      stackup = await runStackup(layers, { color: colors, useOutline: false })
    }
  }

  const units = stackup.top.units === 'in' ? 'in' : 'mm'
  const factor = units === 'in' ? 25.4 : 1
  const widthMm = Number(stackup.top.width) * factor
  const heightMm = Number(stackup.top.height) * factor

  if (!Number.isFinite(widthMm) || !Number.isFinite(heightMm) || widthMm <= 0 || heightMm <= 0) {
    throw new Error(t('error.geometry'))
  }

  const stackupLayers = (stackup.layers ?? []) as ProcessedLayer[]
  const copperLayers = stackupLayers.filter((layer) => layer.type === 'copper').length
  const mappings = activeMappings(files)
  const mappedCopper = mappings.filter((mapping) => mapping.type === 'copper').length
  const effectiveCopper = Math.max(copperLayers, mappedCopper)
  const diagnostics = diagnose(files, effectiveCopper, panelDetected)

  return {
    topSvg: stackup.top.svg,
    bottomSvg: stackup.bottom.svg,
    topSvgFlat: stripMasks(stackup.top.svg),
    bottomSvgFlat: stripMasks(stackup.bottom.svg),
    widthMm,
    heightMm,
    units,
    layers: stackupLayers,
    copperLayers: effectiveCopper,
    drillHits: diagnostics.drillHits,
    minApertureMm: diagnostics.minApertureMm,
    fileCount: files.length,
    warnings: diagnostics.warnings,
    checks: diagnostics.checks,
    panelDetected,
  }
}
